import { findPlaceholders, type TiptapDoc } from "@klarity/schema";
import { ChevronDown, Download } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Menu } from "../../components/ui/Menu";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import { saveBlob } from "../../lib/download";
import { toBlocks } from "../history/diff";

export function ExportMenu({
  docId,
  getContent,
  beforeExport,
  unsaved,
}: {
  docId: string;
  /** Read when Export is chosen, so edit mode checks what is on screen at that moment. */
  getContent: () => TiptapDoc;
  beforeExport?: () => Promise<void>;
  /** Read after beforeExport: changes the server still lacks (offline, conflict, lock lost, refused). */
  unsaved?: () => boolean;
}) {
  const { confirm, element } = useConfirm();
  const [busy, setBusy] = useState(false);

  const run = async (format: "pdf" | "docx") => {
    const left = findPlaceholders(toBlocks(getContent()));
    if (left.length > 0) {
      const ok = await confirm({
        title: `Export with ${left.length} placeholder${left.length === 1 ? "" : "s"} left?`,
        body: (
          <>
            <p>These fields are still unfilled and export as orange fields: {left.join(", ")}.</p>
          </>
        ),
        confirmLabel: "Export anyway",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await beforeExport?.();
      // The server exports what it has saved, so unsaved changes would be silently missing from the file.
      if (
        unsaved?.() &&
        !(await confirm({
          title: "Your latest changes are not saved yet",
          body: "The export is made from the last saved version and will not include them. Do you want to export the last saved version?",
          confirmLabel: "Export last saved version",
        }))
      )
        return;
      const { blob, filename, warnings, warningsTruncated } = await api.exports.download(docId, format);
      saveBlob(blob, filename);
      // Placeholders were confirmed above; the rest are named in words. The server drops warnings past 2 KB.
      const other = warnings
        .filter((w) => !w.startsWith("placeholder:"))
        .map((w) => (w.startsWith("missing-asset:") ? "an image is missing from the export" : w));
      if (warningsTruncated > 0) other.push(`+${warningsTruncated} more`);
      if (other.length > 0) toast(`Exported with warnings: ${other.join(", ")}`, "warning");
    } catch (e) {
      toast(`Export failed: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Menu
        label="Export"
        align="end"
        trigger={
          <Button aria-label="Export" disabled={busy}>
            <Download size={15} /> Export <ChevronDown size={14} />
          </Button>
        }
        items={[
          { id: "pdf", label: "PDF", hint: "House style", onSelect: () => void run("pdf") },
          { id: "docx", label: "Word (DOCX)", hint: "Editable", onSelect: () => void run("docx") },
        ]}
      />
      {element}
    </>
  );
}
